import React, { useEffect, useRef, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { BsCalendarDate } from "react-icons/bs";
import { AiFillCaretDown } from "react-icons/ai";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "../../components/calendartheme.css";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../context/AuthContext";
import { affiliateAPI } from "../../services/AffiliatePartnerPortal/affiliateAPI";

export default function ApplicationsList({ profile }) {
  // console.log(profile._id,"kkkkkkkjjjjjjjjjjjjjjj")
  const { user } = useAuth();
  const datePickerRef = useRef(null);
  const statusDropdownRef = useRef(null);

  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("all");

  const [startDate, endDate] = dateRange;

const statusOptions = [
    { value: "All", label: "All Statuses" },
    { value: "new-leads", label: "New Leads" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "processing", label: "Processing" },
    { value: "submitted", label: "Submitted" },
    { value: "in-review", label: "In Review" },
  ];

  // ✅ Fetch applications from API
  useEffect(() => {
    const fetchApplications = async () => {
      try {
        setLoading(true);
        if (!user?.id) return;

        const response = await affiliateAPI.getAffiliateLeads(profile?._id);
        const fetched = Array.isArray(response?.data) ? response.data : [];
        setApplications(fetched);
        setFilteredApplications(fetched);
      } catch (err) {
        console.error("Error fetching applications:", err);
        toast.error("Failed to fetch applications.");
      } finally {
        setLoading(false);
      }
    };
    fetchApplications();
  }, [user?.id, profile?.contact]);

  // ✅ Filter applications (search + date + status)
  useEffect(() => {
    const filtered = (applications || []).filter((app) => {
      if (!app) return false;

      const matchesSearch =
        app?.applicantName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        app?.phoneNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(app?.loanAmount)?.includes(searchQuery.toLowerCase()) ||
        app?.loanType?.toLowerCase().includes(searchQuery.toLowerCase());

      const createdAt = app?.applicationDate ? new Date(app.applicationDate) : null;
      const matchesDate =
        (!startDate || (createdAt && createdAt >= startDate)) &&
        (!endDate || (createdAt && createdAt <= endDate));

      const matchesStatus =
        selectedStatus === "all" || app?.status?.toLowerCase() === selectedStatus.toLowerCase();

      return matchesSearch && matchesDate && matchesStatus;
    });

    setFilteredApplications(filtered);
  }, [applications, searchQuery, startDate, endDate, selectedStatus]);

  // ✅ Handle Select All
  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    if (newSelectAll) {
      setSelectedApplications(filteredApplications.map((a) => a._id));
    } else {
      setSelectedApplications([]);
    }
  };

  // ✅ Handle Select Single
  const handleSelectApplication = (id) => {
    setSelectedApplications((prev) =>
      prev.includes(id) ? prev.filter((aid) => aid !== id) : [...prev, id]
    );
  };

  // ✅ Handle Status Change
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setIsStatusDropdownOpen(false);
  };

  // ✅ Export selected or all applications to Excel inside ZIP
  const exportToExcelAndZip = async () => {
    const appsToExport =
      selectedApplications.length > 0
        ? filteredApplications.filter((a) => selectedApplications.includes(a._id))
        : filteredApplications;

    if (!appsToExport || appsToExport.length === 0) {
      toast.warn("No applications to export.");
      return;
    }

    const wb = XLSX.utils.book_new();
    const dataForExcel = appsToExport.map((a) => ({
      "Applicant Name": a?.applicantName || "N/A",
      "Phone Number": a?.phoneNumber || "N/A",
      "Loan Type": a?.loanType || "N/A",
      "Loan Amount": a?.loanAmount || "N/A",
      Status: a?.status || "N/A",
      "Date Added": a?.applicationDate ? new Date(a.applicationDate).toLocaleDateString() : "N/A",
      "Recent Update": a?.recentUpdate ? new Date(a.recentUpdate).toLocaleDateString() : "N/A",
      Commission:
        a?.commission !== undefined
          ? a.commission
          : Math.round((Number(a.loanAmount) || 0) * 0.025),
    }));

    const ws = XLSX.utils.json_to_sheet(dataForExcel);
    XLSX.utils.book_append_sheet(wb, ws, "Applications");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const zip = new JSZip();
    zip.file("applications.xlsx", excelBuffer);

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "applications.zip";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ✅ Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsDatePickerOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setIsStatusDropdownOpen(false);
      }
    };

    if (isDatePickerOpen || isStatusDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDatePickerOpen, isStatusDropdownOpen]);

 const getStatusStyle = (status) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "bg-[#4ADE8033] text-[#4ADE80]";
      case "rejected":
        return "bg-[#FF0000] text-red-300";
      case "in-review":
        return "bg-[#CCCCFF33] text-[#CCCCFF]";
      case "processing":
        return "bg-[#423715] text-[#FFCA20]";
      case "submitted":
        return "bg-purple-600 text-purple-300";
      case "new-leads":
        return "bg-gray-600 text-gray-300";
      default:
        return "bg-gray-600 text-gray-300";
    }
  };

  return (
    <div>
      {/* Filters Row */}
      <div className="flex flex-wrap lg:flex-nowrap items-center gap-4 mb-4">
        {/* Search */}
        <div className="relative flex items-center bg-transparent border border-[#333333] rounded-full w-full sm:w-80">
          <FiSearch className="absolute left-4 w-[20px] text-[#F3F3F3]" />
          <input
            type="text"
            placeholder="Search Applications"
            className="bg-transparent text-white rounded-full pl-10 py-2 leading-normal focus:outline-none w-full"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Calendar + Status + Download */}
        <div className="flex justify-between lg:justify-normal w-full px-4 lg:w-2/3">
          {/* Calendar */}
          <div className="relative border border-[#333333] rounded-md" ref={datePickerRef}>
            <button
              className="flex items-center bg-transparent rounded-md px-2 sm:px-3 py-2 focus:outline-none text-sm sm:text-base"
              onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
            >
              <BsCalendarDate className="mr-2 text-[#FFCA20]" />
              {startDate && endDate
                ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                : "Choose Dates"}
            </button>
            {isDatePickerOpen && (
              <div className="absolute z-10 mt-2 left-0 bg-[#1e1e1e] rounded-lg p-2 border border-[#333333]">
                <Calendar
                  selectRange={true}
                  onChange={(update) => {
                    if (update[0] && update[1]) {
                      setDateRange(update);
                      setIsDatePickerOpen(false);
                    }
                  }}
                  value={[startDate, endDate]}
                  calendarType="gregory"
                  prev2Label={null}
                  next2Label={null}
                />
              </div>
            )}
          </div>

          {/* Status Dropdown */}
          <div
            className="relative border border-[#333333] rounded-md lg:ml-5"
            ref={statusDropdownRef}
          >
            <button
              className="flex items-center bg-transparent rounded-md px-3 py-2 focus:outline-none"
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
            >
              Status <AiFillCaretDown className="ml-1 text-[#FFCA20]" />
            </button>
            {isStatusDropdownOpen && (
              <div className="absolute z-10 mt-1 w-48 bg-[#1e1e1e] rounded-md shadow-lg">
                {statusOptions.map((option) => (
                  <button
                    key={option.value}
                    className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-[#2c2c2c]"
                    onClick={() => handleStatusChange(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Download ZIP Button */}
          <button
            onClick={exportToExcelAndZip}
            className="ml-3 px-4 py-2 rounded-md bg-yellow-400 text-black font-bold"
          >
            Download as ZIP
          </button>
        </div>
      </div>

      {/* Bulk Select */}
      {/* <div className="mb-2">
        <label className="inline-flex items-center text-sm text-gray-500">
          <input
            type="checkbox"
            className="appearance-none h-5 w-5 border-2 border-white/20 rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 relative cursor-pointer
            before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
            checked={selectAll}
            onChange={handleSelectAll}
          />
          <span className="ml-2">Select to enable bulk options</span>
        </label>

        {selectedApplications.length > 0 && (
          <div className="mt-2 flex gap-2">
            <span className="text-sm text-gray-400">{selectedApplications.length} selected</span>
            <button className="text-sm text-[#FFCA20] hover:underline">Bulk Action</button>
          </div>
        )}
      </div> */}

      {/* Applications Table */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
        </div>
      ) : (
        <div className="overflow-x-auto scrollbar-hide">
          <table className="min-w-full border-collapse border border-[#333333] rounded-lg overflow-hidden">
            <thead className="bg-[#1e1e1e]">
              <tr>
                <th className="border border-[#333333] px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="appearance-none h-5 w-5 border-2 border-white/20 rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 relative cursor-pointer
                    before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                  />
                </th>
                <th className="border border-[#333333] px-4 py-3 text-xs font-medium text-[#F3F3F3] uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="border border-[#333333] px-4 py-3 text-xs font-medium text-[#F3F3F3] uppercase tracking-wider">
                  Applicant Name
                </th>
                <th className="border border-[#333333] px-4 py-3 text-xs font-medium text-[#F3F3F3] uppercase tracking-wider">
                  Phone Number
                </th>
                <th className="border border-[#333333] px-4 py-3 text-xs font-medium text-[#F3F3F3] uppercase tracking-wider">
                  Loan Amount
                </th>
                <th className="border border-[#333333] px-4 py-3 text-xs font-medium text-[#F3F3F3] uppercase tracking-wider">
                  Recent Update
                </th>
                <th className="border border-[#333333] px-4 py-3 text-xs font-medium text-[#F3F3F3] uppercase tracking-wider">
                  Status
                </th>
                <th className="border border-[#333333] px-4 py-3 text-xs font-medium text-[#F3F3F3] uppercase tracking-wider">
                  Commission (2.5%)
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#1e1e1e] border border-[#333333]">
              {filteredApplications.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-4 text-center text-[#F3F3F3]">
                    No applications found
                  </td>
                </tr>
              ) : (
                filteredApplications.map((app) => {
                  const createdDate = app.applicationDate
                    ? new Date(app.applicationDate).toLocaleString()
                    : "N/A";
                  const recentUpdate = app.recentUpdate
                    ? new Date(app.recentUpdate).toLocaleString()
                    : createdDate;
                  const commission =
                    app.commission !== undefined
                      ? app.commission
                      : Math.round((Number(app.loanAmount) || 0) * 0.025);

                  const formatINR = (num) =>
                    num
                      .toLocaleString("en-IN", {
                        style: "currency",
                        currency: "INR",
                        maximumFractionDigits: 0,
                      })
                      .replace("INR", "₹");

                  return (
                    <tr key={app._id}>
                      <td className="border border-[#333333] px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedApplications.includes(app._id)}
                          onChange={() => handleSelectApplication(app._id)}
                          className="appearance-none h-5 w-5 border-2 border-white/20 rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 relative cursor-pointer
                          before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                        />
                      </td>
                      <td className="border border-[#333333] px-4 py-3 text-sm text-gray-300">
                        {createdDate}
                      </td>
                      <td className="border border-[#333333] px-4 py-3 text-sm text-white">
                        {app.applicantName}
                      </td>
                      <td className="border border-[#333333] px-4 py-3 text-sm text-[#F3F3F3]">
                        {app.phoneNumber}
                      </td>
                      <td className="border border-[#333333] px-4 py-3 text-sm text-[#F3F3F3]">
                        {formatINR(Number(app.loanAmount) || 0)}
                      </td>
                      <td className="border border-[#333333] px-4 py-3 text-sm text-[#F3F3F3]">
                        {recentUpdate}
                      </td>
                      <td className="border border-[#333333] px-4 py-3">
                        <span
                          className={`px-4 py-1 rounded-full text-sm font-semibold ${getStatusStyle(
                            app.status
                          )}`}
                        >
                          {app.status}
                        </span>
                      </td>
                      <td className="border border-[#333333] px-4 py-3 text-sm text-green-400">
                        {app.status === "approved"
                          ? `+ ${formatINR(commission)}`
                          : formatINR(commission)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
